// ─── Flatten helpers ──────────────────────────────────────────────────────────
/**
 * Recursively collect ALL leaf-item ids within a node (or the node itself if leaf).
 */
export function collectDescendantIds(node) {
  if (node.type === "item") return [node.id];
  return (node.children || []).flatMap(collectDescendantIds);
}

/**
 * Flat list of all visible rows for a tree (respects expanded state).
 * Returns objects: { node, depth, parentId }
 */
export function buildVisibleRows(nodes, expandedIds, depth = 0, parentId = null) {
  const rows = [];
  for (const node of nodes) {
    rows.push({ node, depth, parentId });
    if (node.type === "group" && expandedIds.has(node.id)) {
      rows.push(
        ...buildVisibleRows(node.children || [], expandedIds, depth + 1, node.id)
      );
    }
  }
  return rows;
}

/**
 * Filter a tree by search text. Groups are kept if any child matches.
 */
export function filterTree(nodes, query) {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes.reduce((acc, node) => {
    if (node.type === "item") {
      if (node.label.toLowerCase().includes(q)) acc.push(node);
    } else {
      const filteredChildren = filterTree(node.children || [], q);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      } else if (node.label.toLowerCase().includes(q)) {
        acc.push(node);
      }
    }
    return acc;
  }, []);
}

/**
 * Move nodes from source tree to target tree, always preserving folder structure.
 *
 * Rules:
 *  - If a GROUP id is in `ids` → move the entire group as-is.
 *  - If individual ITEM ids within a group are in `ids` → those items are
 *    wrapped in a partial-copy of their parent group and placed in the target.
 *    The source group keeps the remaining children (or is removed if empty).
 *  - Top-level items move directly.
 *  - Groups that already exist in the target have their children merged
 *    (no duplicate group headers, no orphaned items).
 */
export function moveNodes(sourceTree, targetTree, ids, preserveFolders = true) {
  if (!preserveFolders) {
    // Flat mode: items move as top-level nodes, may become orphans
    const collected = [];
    const remaining = removeNodes(sourceTree, ids, collected);
    return { remaining, added: [...targetTree, ...collected] };
  }
  const toAdd = [];
  const remaining = _extractNodes(sourceTree, ids, toAdd);
  const merged = _mergeIntoTree(targetTree, toAdd);
  return { remaining, added: merged };
}

/** @internal */
function _extractNodes(nodes, ids, toAdd) {
  const remaining = [];
  for (const node of nodes) {
    if (node.type === "item") {
      if (ids.has(node.id)) {
        toAdd.push(node);
      } else {
        remaining.push(node);
      }
    } else {
      // group
      if (ids.has(node.id)) {
        // Move entire group
        toAdd.push(node);
      } else {
        // Check children individually
        const movedChildren = [];
        const keptChildren = _extractNodes(node.children || [], ids, movedChildren);

        if (keptChildren.length > 0) {
          // Group stays in source with remaining children
          remaining.push({ ...node, children: keptChildren });
        }
        // If some children were moved, wrap them in a partial group for the target
        if (movedChildren.length > 0) {
          toAdd.push({ ...node, children: movedChildren });
        }
      }
    }
  }
  return remaining;
}

/**
 * Merge `nodes` into `targetTree`.
 * If a group with the same id already exists in the target, its children
 * are merged (new children appended, duplicates skipped).
 * @internal
 */
function _mergeIntoTree(targetTree, nodes) {
  const result = [...targetTree];
  for (const node of nodes) {
    if (node.type === "item") {
      if (!result.find((n) => n.id === node.id)) {
        result.push(node);
      }
    } else {
      const existingIdx = result.findIndex((n) => n.id === node.id);
      if (existingIdx >= 0) {
        const existing = result[existingIdx];
        const existingChildIds = new Set((existing.children || []).map((c) => c.id));
        const newChildren = (node.children || []).filter((c) => !existingChildIds.has(c.id));
        result[existingIdx] = {
          ...existing,
          children: [...(existing.children || []), ...newChildren],
        };
      } else {
        result.push(node);
      }
    }
  }
  return result;
}

/** @deprecated kept for back-compat; prefer moveNodes */
export function removeNodes(nodes, ids, collected) {
  return nodes.reduce((acc, node) => {
    if (ids.has(node.id)) {
      collected.push(node);
      return acc;
    }
    if (node.type === "group") {
      const filteredChildren = removeNodes(node.children || [], ids, collected);
      if (filteredChildren.length > 0 || (node.children || []).length === 0) {
        acc.push({ ...node, children: filteredChildren });
      }
      return acc;
    }
    acc.push(node);
    return acc;
  }, []);
}

/**
 * Find a node by id anywhere in the tree.
 */
export function findNodeById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.type === "group") {
      const found = findNodeById(n.children || [], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Sort a tree.
 * mode: 'asc' | 'desc' | 'folders-asc' | null (original order)
 */
export function sortTree(nodes, mode) {
  if (!mode) return nodes;
  const sorted = [...nodes].sort((a, b) => {
    if (mode === "folders-asc") {
      if (a.type === "group" && b.type !== "group") return -1;
      if (a.type !== "group" && b.type === "group") return 1;
    }
    const dir = mode === "desc" ? -1 : 1;
    return dir * a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
  return sorted.map((node) =>
    node.type === "group"
      ? { ...node, children: sortTree(node.children || [], mode) }
      : node
  );
}

/**
 * Count all leaf items in a tree.
 */
export function countItems(nodes) {
  let c = 0;
  for (const n of nodes) {
    if (n.type === "item") c++;
    else c += countItems(n.children || []);
  }
  return c;
}
